import * as React from "react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import "./Map.css";
import useMediaQuery from "@mui/material/useMediaQuery";
import { lightTheme } from "./lightTheme";
import { darkTheme } from "./darkTheme";
import { DEPTH_BANDS_FT, bandForDepthFt, bandLabel } from "./depthBands";

export default function MapView(props) {
  const { data, setScrollIndex, metric, scrollIndex, pointThreshold, dotSize } =
    props;
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");

  const [map, setMap] = React.useState(null);
  const [crosshairPosition, setCrosshairPosition] = React.useState({
    lat: data.length > 0 ? Number(data[0].latDD) : 0,
    lng: data.length > 0 ? Number(data[0].lonDD) : 0,
  });

  const onLoad = React.useCallback((map) => setMap(map), []);

  React.useEffect(() => {
    if (!map || !data || data.length === 0) {
      return undefined;
    }
    const fit = () => {
      const bounds = new window.google.maps.LatLngBounds();
      data.forEach(({ latDD, lonDD }) =>
        bounds.extend({ lat: Number(latDD), lng: Number(lonDD) })
      );
      window.google.maps.event.trigger(map, "resize");
      map.fitBounds(bounds);
    };
    const div = map.getDiv();
    if (div.offsetWidth > 0) {
      fit();
      return undefined;
    }
    // The viewer commits the whole track layout in one render, so the map can
    // mount while its container is still 0 px wide — and a map initialized at
    // zero size never draws tiles. Wait for real width before fitting.
    const observer = new ResizeObserver(() => {
      if (div.offsetWidth > 0) {
        observer.disconnect();
        fit();
      }
    });
    observer.observe(div);
    return () => observer.disconnect();
  }, [map, data]);

  React.useEffect(() => {
    if (data && map && data[scrollIndex]) {
      let coords = {
        lat: Number(data[scrollIndex].latDD),
        lng: Number(data[scrollIndex].lonDD),
      };
      map.panTo(coords);
      setCrosshairPosition(coords);
    }
    // eslint-disable-next-line
  }, [data, scrollIndex]);

  // data[].depth is already in display units; bands are defined in feet
  const toFeet = (depth) => (metric ? depth * 3.28084 : depth);

  // All soundings are drawn on one canvas overlay — thousands of individual
  // Marker elements make Google Maps freeze while zooming.
  React.useEffect(() => {
    if (
      !map ||
      !window.google?.maps?.OverlayView ||
      !data ||
      data.length === 0
    ) {
      return undefined;
    }
    const scale = Number(dotSize) > 0 ? Number(dotSize) : 2;
    const alarmFt = toFeet(Number(pointThreshold) || 0);
    const points = data.map(({ latDD, lonDD, depth }) => {
      const depthFt = toFeet(depth);
      return {
        latLng: new window.google.maps.LatLng(Number(latDD), Number(lonDD)),
        color: bandForDepthFt(depthFt).color,
        alarmed: alarmFt > 0 && depthFt <= alarmFt,
      };
    });

    const canvas = document.createElement("canvas");
    canvas.style.position = "absolute";
    canvas.style.pointerEvents = "none";

    const overlay = new window.google.maps.OverlayView();
    overlay.onAdd = function () {
      this.getPanes().overlayLayer.appendChild(canvas);
    };
    overlay.draw = function () {
      const projection = this.getProjection();
      const bounds = map.getBounds();
      if (!projection || !bounds) {
        return;
      }
      const mapDiv = map.getDiv();
      const width = mapDiv.offsetWidth;
      const height = mapDiv.offsetHeight;
      const topLeft = projection.fromLatLngToDivPixel(
        new window.google.maps.LatLng(
          bounds.getNorthEast().lat(),
          bounds.getSouthWest().lng()
        )
      );
      const ratio = window.devicePixelRatio || 1;
      canvas.style.left = topLeft.x + "px";
      canvas.style.top = topLeft.y + "px";
      canvas.style.width = width + "px";
      canvas.style.height = height + "px";
      canvas.width = width * ratio;
      canvas.height = height * ratio;
      const ctx = canvas.getContext("2d");
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      ctx.clearRect(0, 0, width, height);
      const margin = 20;
      for (const point of points) {
        const px = projection.fromLatLngToDivPixel(point.latLng);
        const x = px.x - topLeft.x;
        const y = px.y - topLeft.y;
        if (
          x < -margin ||
          y < -margin ||
          x > width + margin ||
          y > height + margin
        ) {
          continue;
        }
        const radius = point.alarmed ? scale * 1.8 : scale;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, 2 * Math.PI);
        ctx.fillStyle = point.color;
        ctx.fill();
        if (point.alarmed) {
          ctx.lineWidth = 1.5;
          ctx.strokeStyle = "#ffffff";
          ctx.stroke();
        }
      }
    };
    overlay.onRemove = function () {
      canvas.remove();
    };
    overlay.setMap(map);

    // Click selects the nearest sounding, like the old per-marker onClick
    const clickListener = map.addListener("click", (event) => {
      const projection = overlay.getProjection();
      if (!projection || !event.latLng) {
        return;
      }
      const clickPx = projection.fromLatLngToDivPixel(event.latLng);
      let best = -1;
      let bestDistSq = Infinity;
      points.forEach((point, index) => {
        const px = projection.fromLatLngToDivPixel(point.latLng);
        const dx = px.x - clickPx.x;
        const dy = px.y - clickPx.y;
        const distSq = dx * dx + dy * dy;
        if (distSq < bestDistSq) {
          bestDistSq = distSq;
          best = index;
        }
      });
      const tolerance = Math.max(10, scale * 3);
      if (best >= 0 && bestDistSq <= tolerance * tolerance) {
        setScrollIndex(best);
      }
    });

    return () => {
      clickListener.remove();
      overlay.setMap(null);
    };
    // eslint-disable-next-line
  }, [map, data, metric, pointThreshold, dotSize]);

  var image = {
    url: "https://maps.gstatic.com/mapfiles/ms2/micons/red.png",
    scaledSize: new window.google.maps.Size(24, 24),
    // anchor: new window.google.maps.Point(12, 12),
  };

  const legendStyle = {
    position: "absolute",
    right: 8,
    bottom: 24,
    zIndex: 2,
    padding: "6px 10px",
    borderRadius: 6,
    fontSize: 11,
    lineHeight: "16px",
    backgroundColor: prefersDarkMode
      ? "rgba(40, 40, 40, 0.85)"
      : "rgba(255, 255, 255, 0.85)",
    color: prefersDarkMode ? "#eee" : "#222",
    boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
  };

  return (
    <div style={{ position: "relative", height: "100%", width: "100%" }}>
      <GoogleMap
        onLoad={onLoad}
        mapContainerClassName="map-container"
        options={{
          mapTypeId: "terrain",
          scrollwheel: false,
          zoomControl: true,
          streetViewControl: false,
          styles: prefersDarkMode ? darkTheme : lightTheme,
          mapTypeControlOptions: {
            style: window.google.maps.MapTypeControlStyle.DROPDOWN_MENU,
          },
          zoomControlOptions: {
            position: window.google.maps.ControlPosition.LEFT_BOTTOM,
          },
        }}
      >
        {data.length > 0 && crosshairPosition && data[scrollIndex] && (
          <Marker position={crosshairPosition} icon={image}></Marker>
        )}
      </GoogleMap>
      <div style={legendStyle} aria-label="Depth legend">
        <div style={{ fontWeight: 600, marginBottom: 2 }}>
          Depth ({metric ? "m" : "ft"})
        </div>
        {DEPTH_BANDS_FT.map((band) => (
          <div
            key={band.color}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: band.color,
                border: "1px solid rgba(255,255,255,0.7)",
                flexShrink: 0,
              }}
            />
            <span>{bandLabel(band, metric)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
